<template>
  <b-modal id="supplement-review-modal" hide-footer title="Đánh giá buổi học" size="xl" centered no-close-on-backdrop @show="initReviews">
    <div class="review-modal-body">
      <div v-if="presentStudents.length" class="review-grid">
        <div v-for="row in reviewRows" :key="row.id || row.code" class="review-card">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div class="student-code">{{ row.code || '-' }}</div>
              <div class="student-name">{{ row.name || '-' }}</div>
            </div>
            <el-select v-model="row.result" placeholder="Chọn đánh giá" size="small" style="width: 160px">
              <el-option v-for="option in reviewOptions" :key="option.value" :label="option.label" :value="option.value" />
            </el-select>
          </div>
          <el-input
            v-model="row.note"
            type="textarea"
            rows="4"
            placeholder="Nhập nhận xét chi tiết..."
          />
        </div>
      </div>
      <div v-else class="empty-state">Không có học viên có mặt để đánh giá</div>
    </div>
    <div class="review-footer">
      <button type="button" class="btn btn-secondary mr-3" @click="closeModal">Hủy</button>
      <button type="button" class="btn btn-primary" :disabled="!canSave || loading" @click="saveReview">
        <i v-if="loading" class="el-icon-loading"></i> Lưu đánh giá
      </button>
    </div>
  </b-modal>
</template>

<script>
import { SAVE_SUPPLEMENTARY_TUTOR_EVALUATION } from "@/core/services/store/tutor/tutor.module";
import { updateSupplementTutorReviews } from "@/core/mock/tutorSupplement.mock";

export default {
  name: 'SupplementTutorReviewModal',
  props: {
    task: {
      type: Object,
      default: () => ({})
    },
    supplementaryStudents: {
      type: Array,
      default: () => []
    },
    supplementaryStudentsLoaded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      reviewRows: [],
      reviewOptions: [
        { label: 'Đạt', value: 1 },
        { label: 'Không đạt', value: 2 },
        { label: 'Không làm bài tập', value: 3 }
      ]
    };
  },
  computed: {
    students() {
      const students = this.supplementaryStudentsLoaded
        ? this.supplementaryStudents
        : (this.task?.supplement_students || this.task?.detail?.students || this.task?.students || []);

      if (students.length) {
        return students;
      }

      if (this.supplementaryStudentsLoaded) {
        return [];
      }

      const totalStudents = Number(this.task?.detail?.total_students || 0);

      return Array.from({ length: totalStudents }, (_, index) => ({
        id: `student-${index + 1}`,
        code: `HV${index + 1}`,
        name: `Học viên ${index + 1}`,
        attendance_status: 'present'
      }));
    },
    presentStudents() {
      return this.students.filter((student) => {
        const status = student.attendance_status || student.status;
        return Number(status) === 1 || status === 'present';
      });
    },
    canSave() {
      return this.reviewRows.length > 0 && this.reviewRows.every((student) => student.result);
    }
  },
  methods: {
    getStudentKey(student) {
      return String(student.id || student.student_id || student.code || student.student_code);
    },
    initReviews() {
      const savedReviews = this.task?.work_results?.supplement_reviews || this.task?.supplement_reviews || [];
      this.reviewRows = this.presentStudents.map((student) => {
        const key = this.getStudentKey(student);
        const savedReview = savedReviews.find((item) => String(item.id || item.student_id) === key);

        return {
          id: student.id || student.student_id,
          code: this.formatStudentCode(student),
          name: student.name || student.student_name,
          result: savedReview?.learning_result || student.learning_result || '',
          note: savedReview?.result_comment || student.result_comment || ''
        };
      });
    },
    formatStudentCode(student) {
      const code = student.code ?? student.student_code ?? student.user_id;
      if (code === null || code === undefined || code === '') {
        return null;
      }

      const numericCode = Number(code);
      return Number.isFinite(numericCode) ? numericCode + 100000 : code;
    },
    closeModal() {
      this.$bvModal.hide('supplement-review-modal');
    },
    buildPayload() {
      return {
        students: this.reviewRows.map((student) => ({
        id: student.id,
        learning_result: student.result,
        result_comment: student.note || ''
        }))
      };
    },
    async saveReview() {
      try {
        this.loading = true;
        const data = this.buildPayload();
        const idTask = this.task?.id || this.$route.params.id;

        if (String(idTask).startsWith('SUP-')) {
          updateSupplementTutorReviews(idTask, data.students);
        } else {
          await this.$store.dispatch(SAVE_SUPPLEMENTARY_TUTOR_EVALUATION, {
            idTask,
            data
          });
        }

        this.closeModal();
        this.$notify({
          title: 'Success',
          message: 'Lưu đánh giá buổi học thành công',
          type: 'success'
        });
        this.$emit('loadDetail');
      } catch (e) {
        this.$notify.error({
          title: 'Error',
          message: 'Đã có lỗi xảy ra'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.review-modal-body {
  background: #f3f4f6;
  margin: -16px;
  padding: 24px;
  max-height: 620px;
  overflow: auto;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.review-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}

.student-code {
  color: #0061A4;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.student-name {
  color: #181C1E;
  font-size: 14px;
  font-weight: 600;
}

.empty-state {
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  text-align: center;
}

.review-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 32px;
}

@media (max-width: 768px) {
  .review-grid {
    grid-template-columns: 1fr;
  }
}
</style>
